import { z } from 'zod';
/**
 * Call-out and refill (§14.5, slice-03 §7, exit criterion 7).
 *
 * A call-out empties the caregiver, keeps the visit SCHEDULED, flags CALLOUT,
 * and starts a clock. Offers go out in waves to matching's ranked list; the
 * first caregiver to claim wins by a row lock, and the rest are told the shift
 * is already filled. The clock stops at the refill however it happened — a
 * claim or an assignment from the board — and `GET /v1/schedule/callouts`
 * reports it: median seconds, and the share inside §14.5's 90.
 *
 * SMS and push delivery of offers is Slice 5. Until then an offer is read in
 * the field app, from `GET /v1/field/offers`, which is where the deep link
 * will land.
 */
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-10-06 — four digits, two, two.');
export const calloutRequestSchema = z.object({
    version: z.number().int().min(0),
    reason: z
        .string()
        .trim()
        .min(1, 'Say why the caregiver cannot make it. It stays with the call-out.')
        .max(2_000),
});
export const offerTierSchema = z.enum(['TOP_5', 'TOP_15', 'ALL_ELIGIBLE']);
export const offerShiftSchema = z.object({ tier: offerTierSchema });
export const offerStateSchema = z.enum([
    'PENDING',
    'OFFERED',
    'VIEWED',
    'CLAIMED',
    'DECLINED',
    'EXPIRED',
    'WITHDRAWN',
]);
export const shiftOfferSchema = z.object({
    id: z.string().uuid(),
    visitId: z.string().uuid(),
    caregiverId: z.string().uuid(),
    caregiverName: z.string(),
    wave: z.number().int(),
    rank: z.number().int(),
    score: z.number().int(),
    explanation: z.string(),
    offeredAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    state: offerStateSchema,
    respondedAt: z.string().datetime().nullable(),
});
export const shiftOfferListSchema = z.object({
    offers: z.array(shiftOfferSchema),
    /** Ranked caregivers left out because their wave would open after the visit starts. */
    notReached: z.number().int(),
});
export const calloutListQuerySchema = z
    .object({ from: isoDate, to: isoDate })
    .refine((query) => query.to >= query.from, {
    path: ['to'],
    message: 'The last day can’t be before the first.',
});
export const refillMetricsSchema = z.object({
    callouts: z.number().int(),
    filled: z.number().int(),
    open: z.number().int(),
    /** Median, never mean. Null when nothing has been filled yet. */
    medianSecondsToFill: z.number().int().nullable(),
    targetSeconds: z.number().int(),
    withinTarget: z.number().int(),
    withinTargetPct: z.number().int().nullable(),
});
export const calloutBoardSchema = z.object({
    callouts: z.array(z.object({
        id: z.string().uuid(),
        visitId: z.string().uuid(),
        visitStartsAt: z.string().datetime(),
        clientName: z.string(),
        calledOutCaregiverName: z.string(),
        source: z.enum(['COORDINATOR', 'CAREGIVER']),
        reason: z.string(),
        calledOutAt: z.string().datetime(),
        filledAt: z.string().datetime().nullable(),
        fillSource: z.enum(['CLAIM', 'ASSIGNMENT']).nullable(),
        filledCaregiverName: z.string().nullable(),
        /** Null while open — never zero. */
        secondsToFill: z.number().int().nullable(),
        /** Open, and inside the escalation lead time before the start. */
        needsEscalation: z.boolean(),
    })),
    metrics: refillMetricsSchema,
});
/** An offer as the caregiver sees it: when, where roughly, and what it pays attention to. */
export const fieldOfferSchema = z.object({
    id: z.string().uuid(),
    visitId: z.string().uuid(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    timezone: z.string(),
    localDate: isoDate,
    localStartTime: z.string(),
    localEndTime: z.string(),
    serviceCode: z.string(),
    city: z.string().nullable(),
    state: offerStateSchema,
    expiresAt: z.string().datetime(),
});
export const fieldOfferListSchema = z.object({ offers: z.array(fieldOfferSchema) });
export const fieldClaimResultSchema = z.object({
    offerId: z.string().uuid(),
    visitId: z.string().uuid(),
    state: offerStateSchema,
});
//# sourceMappingURL=refill.js.map